import { Subject, takeUntil, Observable } from 'rxjs';

import { ComponentRef } from '@angular/core';
import { GlobalPositionStrategy } from '@angular/cdk/overlay';
import { DialogRef, DialogConfig } from '@angular/cdk/dialog';

import { AppDialogPosition } from './dialog-config';

export class AppDialogRef<T, R = any> {
  componentInstance!: T;

  readonly componentRef!: ComponentRef<T> | null;

  id: string;

  disableClose: boolean | undefined;

  private readonly _canceledSubject: Subject<void> = new Subject<void>();
  private readonly _submittedSubject: Subject<R> = new Subject<R>();

  readonly canceled = this._canceledSubject.asObservable();
  readonly submitted = this._submittedSubject.asObservable();

  constructor(
    private _ref: DialogRef<R, T>,
    config: DialogConfig,
  ) {
    this.componentRef = _ref.componentRef ?? null;
    this.disableClose = config.disableClose;
    this.id = _ref.id;

    _ref.closed.subscribe(() => {
      this._canceledSubject.complete();
      this._submittedSubject.complete();
    });

    this.backdropClick.pipe(takeUntil(this.closed)).subscribe(() => {
      if (!this.disableClose) {
        this.cancel();
      }
    });
  }

  get closed(): Observable<R | undefined> {
    return this._ref.closed;
  }

  get backdropClick(): Observable<MouseEvent> {
    return this._ref.backdropClick;
  }

  close(dialogResult?: R): void {
    this._ref.close(dialogResult);
  }

  cancel(): void {
    this._canceledSubject.next();
    this.close();
  }

  submit(data: R): void {
    this._submittedSubject.next(data);
    this.close(data);
  }

  updatePosition(position?: AppDialogPosition): this {
    const strategy = this._ref.config.positionStrategy as GlobalPositionStrategy;

    if (position && (position.left || position.right)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      position.left ? strategy.left(position.left) : strategy.right(position.right);
    } else {
      strategy.centerHorizontally();
    }

    if (position && (position.top || position.bottom)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      position.top ? strategy.top(position.top) : strategy.bottom(position.bottom);
    } else {
      strategy.centerVertically();
    }

    this._ref.updatePosition();

    return this;
  }

  updateSize(width = '', height = ''): this {
    this._ref.updateSize(width, height);
    return this;
  }

  addPanelClass(classes: string | string[]): this {
    this._ref.addPanelClass(classes);
    return this;
  }

  removePanelClass(classes: string | string[]): this {
    this._ref.removePanelClass(classes);
    return this;
  }
}
